import { useEffect, useState } from 'react';
import { NavLink, generatePath, useLocation } from 'react-router-dom';
import {
  Checkbox,
  IconSelected,
  IconChat,
  IconDownload,
  IconTrash,
  IconClock,
  IconAttention,
  IconBlock,
  PermissionEnum,
  ClickOutsideDetector,
  Popup,
  IconEdit,
} from '@ui/src';
import { useAppDispatch, useAppSelector } from '@store';
import {
  useLazyGetCustomerDocumentFileQuery,
  useDeleteCustomerDocumentMutation,
  useChangeCustomerDocumentsStatusMutation,
  useLazyGetCustomerOrganizationDocumentsStatsQuery,
} from '../../../hoc/customers/store/services/customers';
import {
  CustomerType,
  CustomerDocumentStatus,
  CustomerStatus,
} from '../../../hoc/customers/types';
import { deleteIsSuccess } from '../../../hoc/customers/store/slices/documents/documents';
import { DocumentRowTypes } from './types';

import ROUTES from '../../../routes';

import cn from './documents.module.css';

const DocumentRow = ({
  onSelect,
  document: documentItem,
  type,
  lastChild,
  customerId,
  customerType,
  emptyDocument,
  setRenamePopupVisible,
  setSelectedDocumentToRename,
  status,
  updateExpiredDocumentsState,
}: DocumentRowTypes): React.JSX.Element => {
  const [subMenuVisible, setSubMenuVisible] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState<
    CustomerDocumentStatus | undefined
  >();
  const [deletePopupVisible, setDeletePopupVisible] = useState(false);

  const { activePermissions } = useAppSelector(
    (state) => state.customersDictionaries,
  );

  const [
    changeCustomerDocumentsStatus,
    { isLoading: statusChangeInProgress, isSuccess: statusChangeIsSuccess },
  ] = useChangeCustomerDocumentsStatusMutation();
  const [getStats] = useLazyGetCustomerOrganizationDocumentsStatsQuery();

  const dispatch = useAppDispatch();

  const { search } = useLocation();

  const [
    getCustomerDocumentFile,
    { isLoading, isFetching, isSuccess, isError },
  ] = useLazyGetCustomerDocumentFileQuery();
  const [
    deleteFile,
    {
      isLoading: deleteInProgress,
      isSuccess: deleteSuccess,
      isError: deleteError,
    },
  ] = useDeleteCustomerDocumentMutation();

  const getFile = async () => {
    const response = await getCustomerDocumentFile({
      id: customerId,
      documentId: documentItem.id,
    });

    if (response.data) {
      const hiddenElement = document.createElement('a');

      const url = window.URL || window.webkitURL;

      // @ts-ignore
      const blob = url.createObjectURL(response.data);
      hiddenElement.href = blob;
      hiddenElement.target = '_blank';
      hiddenElement.download = documentItem.name;
      hiddenElement.click();
    }
  };

  const onDelete = async () => {
    const response = await deleteFile({
      id: customerId,
      documentId: documentItem.id,
    });

    if (response) {
      dispatch(
        deleteIsSuccess({ ids: [documentItem.id], id: documentItem.id }),
      );

      setDeletePopupVisible(false);

      getStats(customerId);
    }
  };

  let link;

  if (customerType === CustomerType.INDIVIDUAL) {
    link = generatePath(ROUTES.CUSTOMERS_INDIVIDUAL_DOCUMENTS_INFO, {
      customerId,
      documentId: documentItem.id,
    });
  } else {
    link = generatePath(ROUTES.CUSTOMERS_CORPORATE_DOCUMENTS_INFO, {
      customerId,
      documentId: documentItem.id,
    });
  }

  let statusIcon: React.JSX.Element;

  if (documentItem.status === CustomerDocumentStatus.ACCEPTED) {
    statusIcon = (
      <div className={cn.documentRowAccepted}>
        <IconSelected
          className={cn.documentRowStatusIcon}
          height={9}
          width={13}
        />
      </div>
    );
  } else if (documentItem.status === CustomerDocumentStatus.EXPIRED) {
    statusIcon = (
      <div className={cn.documentRowExpired}>
        <IconAttention />
      </div>
    );
  } else if (documentItem.status === CustomerDocumentStatus.REJECTED) {
    statusIcon = (
      <div className={cn.documentRowRejected}>
        <IconBlock />
      </div>
    );
  } else {
    statusIcon = <div className={cn.documentRowNew} />;
  }

  useEffect(() => {
    if (
      isSuccess ||
      isError ||
      deleteSuccess ||
      deleteError ||
      statusChangeIsSuccess
    ) {
      setSubMenuVisible(false);

      if (statusChangeIsSuccess) {
        setSelectedStatus(undefined);
      }

      if (documentItem?.status === CustomerDocumentStatus.EXPIRED) {
        updateExpiredDocumentsState();
      }
    }
  }, [isSuccess, isError, deleteSuccess, deleteError, statusChangeIsSuccess]);

  const documentRenameAvailable =
    customerType === CustomerType.INDIVIDUAL
      ? activePermissions?.includes(
          PermissionEnum.INDIVIDUAL_CUSTOMERS_DOCUMENTS_EDIT_ACTION,
        )
      : activePermissions?.includes(
          PermissionEnum.CORPORATE_CUSTOMERS_DOCUMENTS_EDIT_ACTION,
        );

  return (
    <>
      <Popup
        title={`Delete ${documentItem.name} documents?`}
        onCancelClick={() => {
          setDeletePopupVisible(false);
        }}
        onOkClick={onDelete}
        cancelButtonText="Cancel"
        okButtonText={deleteInProgress ? 'Loading...' : 'Delete'}
        isVisible={deletePopupVisible}
        isLoading={deleteInProgress}
      />
      <div
        className={`${cn.documentRow} ${lastChild ? cn.documentRowLast : ''} ${
          emptyDocument ? cn.emptyDocumentRow : ''
        }`}
        id={documentItem.id}
        style={{ zIndex: subMenuVisible ? 5 : 'inherit' }}
      >
        <NavLink
          to={`${link}${search || ''}`}
          className={`${cn.documentRowLink} ${
            emptyDocument ? cn.documentRowLinkEmpty : ''
          }`}
        />
        <div className={cn.documentRowLeft}>
          <div className={cn.documentRowLeftContent}>
            <div
              className={`${cn.documentRowLeftContentItem}  ${cn.documentRowLeftDate}`}
            >
              <Checkbox
                name={`checkbox-${documentItem.id}`}
                title={new Date(documentItem.createdAt).toLocaleDateString()}
                onChange={(e) =>
                  onSelect(type, e.target.checked, documentItem.id)
                }
                type="checkbox"
                checked={documentItem.isSelected}
                disabled={emptyDocument || status === CustomerStatus.CLOSED}
                labelClassName={`${cn.selectDocumentLabel} ${
                  emptyDocument ? cn.selectDocumentLabelEmpty : ''
                }`}
              />
            </div>
            <div className={cn.documentRowLeftContentItem}>
              <div className={cn.documentRowLeftContentItemValue}>
                {documentItem.name}
              </div>
            </div>
          </div>
        </div>
        <div className={cn.documentRowRight}>
          <div className={cn.documentRowRightContentLeft}>
            <div
              className={`${cn.documentsRowDate} ${
                emptyDocument ? cn.documentsRowDateEmpty : ''
              } ${
                documentItem.warning ||
                documentItem.status === CustomerDocumentStatus.EXPIRED
                  ? cn.documentDateExpired
                  : ''
              }`}
            >
              {documentItem.expirationDate
                ? new Date(documentItem.expirationDate).toLocaleDateString()
                : '-'}
              {(documentItem.warning ||
                documentItem.status === CustomerDocumentStatus.EXPIRED) && (
                <IconClock className={cn.expiredIcon} />
              )}
            </div>
            <div
              className={`${cn.documentRowStatus} ${
                emptyDocument ? cn.documentRowStatusEmpty : ''
              } ${
                documentItem.status === CustomerDocumentStatus.NEW
                  ? cn.documentRowStatusNew
                  : ''
              }`}
            >
              <span>
                {documentItem.status}
                {statusIcon}
              </span>
            </div>
          </div>
          <div className={cn.documentRowRightContentRight}>
            <div className={cn.documentsRowComments}>
              {documentItem?.countComments &&
                documentItem?.countComments.count > 0 && (
                  <>
                    <IconChat
                      className={cn.documentsRowCommentIcon}
                      width={14}
                      height={14}
                    />
                    {documentItem?.countComments.count}
                  </>
                )}
            </div>
            {!emptyDocument && (
              <ClickOutsideDetector setOptionsListVisible={setSubMenuVisible}>
                <div className={cn.documentsRowMenuWrapper}>
                  <button
                    type="button"
                    aria-label="Show menu"
                    className={`${cn.documentsRowMenuButton} ${
                      subMenuVisible ? cn.documentsRowMenuButtonActive : ''
                    }`}
                    onClick={() => setSubMenuVisible(!subMenuVisible)}
                  >
                    <span />
                  </button>
                  {subMenuVisible && (
                    <ul className={cn.documentsRowMenu}>
                      {((customerType === CustomerType.INDIVIDUAL &&
                        activePermissions?.includes(
                          PermissionEnum.INDIVIDUAL_CUSTOMERS_DOCUMENTS_UPDATE_STATUS_ACTION,
                        )) ||
                        (customerType === CustomerType.CORPORATE &&
                          activePermissions?.includes(
                            PermissionEnum.CORPORATE_CUSTOMERS_DOCUMENTS_UPDATE_STATUS_ACTION,
                          ))) && (
                        <>
                          {documentItem.status !==
                            CustomerDocumentStatus.ACCEPTED &&
                            documentItem.status !==
                              CustomerDocumentStatus.EXPIRED && (
                              <li>
                                <button
                                  type="button"
                                  className={cn.documentsMenuButton}
                                  disabled={statusChangeInProgress}
                                  onClick={() => {
                                    setSelectedStatus(
                                      CustomerDocumentStatus.ACCEPTED,
                                    );

                                    changeCustomerDocumentsStatus({
                                      id: customerId,
                                      body: {
                                        ids: [documentItem.id],
                                        status: CustomerDocumentStatus.ACCEPTED,
                                      },
                                    });
                                  }}
                                >
                                  <IconSelected
                                    className={`${cn.documentsMenuButtonIcon} ${cn.documentAcceptIcon}`}
                                  />
                                  {statusChangeInProgress &&
                                  selectedStatus ===
                                    CustomerDocumentStatus.ACCEPTED
                                    ? 'Loading...'
                                    : 'Accept'}
                                </button>
                              </li>
                            )}

                          {documentItem.status !==
                            CustomerDocumentStatus.REJECTED &&
                            documentItem.status !==
                              CustomerDocumentStatus.EXPIRED && (
                              <li>
                                <button
                                  type="button"
                                  className={cn.documentsMenuButton}
                                  disabled={statusChangeInProgress}
                                  onClick={() => {
                                    setSelectedStatus(
                                      CustomerDocumentStatus.REJECTED,
                                    );

                                    changeCustomerDocumentsStatus({
                                      id: customerId,
                                      body: {
                                        ids: [documentItem.id],
                                        status: CustomerDocumentStatus.REJECTED,
                                      },
                                    });
                                  }}
                                >
                                  <IconBlock
                                    className={`${cn.documentsMenuButtonIcon} ${cn.documentRejectIcon}`}
                                  />
                                  {statusChangeInProgress &&
                                  selectedStatus ===
                                    CustomerDocumentStatus.REJECTED
                                    ? 'Loading...'
                                    : 'Reject'}
                                </button>
                              </li>
                            )}
                        </>
                      )}
                      {documentRenameAvailable && (
                        <li>
                          <button
                            type="button"
                            className={cn.documentsMenuButton}
                            onClick={() => {
                              setRenamePopupVisible(true);
                              setSelectedDocumentToRename(documentItem);
                            }}
                          >
                            <IconEdit className={cn.documentsMenuButtonIcon} />
                            Rename
                          </button>
                        </li>
                      )}

                      <li>
                        <NavLink
                          to={`${link}#comments`}
                          className={`${cn.documentsMenuButton} ${cn.documentMenuButtonLink}`}
                        >
                          <IconChat
                            className={`${cn.documentsMenuButtonIcon} ${cn.documentCommentIcon}`}
                          />
                          Comments
                        </NavLink>
                      </li>
                      <li>
                        <button
                          type="button"
                          className={cn.documentsMenuButton}
                          onClick={() => getFile()}
                          disabled={isLoading || isFetching}
                        >
                          <IconDownload
                            className={cn.documentsMenuButtonIcon}
                          />
                          {isLoading || isFetching ? 'Loading...' : 'Download'}
                        </button>
                      </li>
                      {((customerType === CustomerType.INDIVIDUAL &&
                        activePermissions?.includes(
                          PermissionEnum.INDIVIDUAL_CUSTOMERS_DOCUMENTS_DELETE_ACTION,
                        )) ||
                        (customerType === CustomerType.CORPORATE &&
                          activePermissions?.includes(
                            PermissionEnum.CORPORATE_CUSTOMERS_DOCUMENTS_DELETE_ACTION,
                          ))) && (
                        <li>
                          <button
                            type="button"
                            className={cn.documentsMenuButton}
                            disabled={deleteInProgress}
                            onClick={() => setDeletePopupVisible(true)}
                          >
                            <IconTrash
                              className={`${cn.documentsMenuButtonIcon} ${cn.documentDeleteIcon}`}
                            />
                            {deleteInProgress ? 'Loading...' : 'Delete'}
                          </button>
                        </li>
                      )}
                    </ul>
                  )}
                </div>
              </ClickOutsideDetector>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default DocumentRow;
